/**
 * Gemeinsamer Formularblock „Schreiben + Versandkanal" für Kündigung, Ruhezeit und Mahnung.
 * Vereinheitlicht: Variantenauswahl, Kanal-Einzelauswahl als Kacheln (E-Mail / Brief (Druck) /
 * Brief-Versand-Integrationen), E-Mail-Verfügbarkeits-Validierung und Merken des zuletzt
 * gewählten Kanals. Fachlich gilt überall genau EIN Versandkanal pro Vorgang.
 *
 * Gewählt wird nur noch die **Variante** — der Aufbau des Schreibens steht im System, eine
 * Vorlagenliste gibt es deshalb nicht.
 *
 * „vor dem Versand bearbeiten" gibt es sehr wohl (`showEditBeforeSend`), aber anders als früher:
 * Es öffnet keinen zweiten Editor, sondern dieselbe Fläche wie die Textpflege — mit den Daten
 * dieses Vorgangs. Geändert wird nur der Wortlaut, und nur für diese eine Sendung; der gepflegte
 * Text bleibt unberührt.
 */
import { AuditOutlined, MailOutlined, PrinterOutlined, SendOutlined } from "@ant-design/icons";
import { Checkbox, Form, Select, Typography } from "../../../../../../shared/frontend/ui";
import type { FormInstance } from "../../../../../../shared/frontend/ui";
import React from "react";
import { CHANNEL_EMAIL_LC, CHANNEL_LETTER_LC } from "../../app/constants/communicationChannel";
import { useAuth } from "../../app/auth/useAuth";
import { spacing } from "../../app/design-system";
import { useI18n } from "../../app/i18n/useI18n";
import { OutcomeTileSelector, type OutcomeTileOption } from "../OutcomeTileSelector";
import { ResponsiveFormGrid } from "../ui/antd";
import { DELIVERY_CHANNEL_PRINT } from "../../app/constants/deliveryChannel";

const DELIVERY_RANK_INTEGRATION = 2;
const DELIVERY_RANK_OTHER = 3;

/** Einheitliches Kanal-Vokabular der UI. Mahnung mappt Backend-„letter" an der Drawer-Grenze auf „print". */
export type DeliveryChannel = "email" | "print" | `integration:${string}` | "debt_collection";

export type DeliveryIntegration = { id: string; displayName: string };

/**
 * Backend-Kanal „letter" heißt im gemeinsamen UI-Vokabular „print" (Brief (Druck));
 * alle anderen Werte (email, integration:<id>, debt_collection) sind identisch.
 */
export function toUiDeliveryChannel(channel: string): DeliveryChannel {
  const normalized = channel.toLowerCase();
  return (normalized === CHANNEL_LETTER_LC ? "print" : normalized) as DeliveryChannel;
}

export function toBackendDeliveryChannel(channel: DeliveryChannel): string {
  return channel === "print" ? CHANNEL_LETTER_LC : channel;
}

/**
 * Verbindliche Reihenfolge für Versandkanäle in m1member:
 * E-Mail, lokaler Briefdruck, danach angebundene Versand-Schnittstellen.
 * Sonstige fachliche Sonderkanäle (z. B. Inkasso) stehen dahinter.
 *
 * Die Funktion versteht sowohl das UI-Vokabular „print" als auch den
 * Backend-Wert „letter", damit auch API-gelieferte Listen zentral sortiert werden.
 */
export function orderDeliveryChannels<T extends string>(channels: readonly T[]): T[] {
  const rank = (channel: string): number => {
    const normalized = channel.toLowerCase();
    if (normalized === CHANNEL_EMAIL_LC) return 0;
    if (normalized === DELIVERY_CHANNEL_PRINT || normalized === CHANNEL_LETTER_LC) return 1;
    if (normalized.startsWith("integration:")) return DELIVERY_RANK_INTEGRATION;
    return DELIVERY_RANK_OTHER;
  };

  return channels
    .map((channel, index) => ({ channel, index }))
    .sort((left, right) => rank(left.channel) - rank(right.channel) || left.index - right.index)
    .map(({ channel }) => channel);
}

export type DeliveryVariantOption = { value: string; label: React.ReactNode; disabled?: boolean };

export type EmailChannelValidation = { isValid: boolean; errors: string[] } | null;

export type DeliveryChannelTileOptionsParams = {
  integrations: DeliveryIntegration[];
  personEmail?: string | null;
  emailValidation?: EmailChannelValidation;
  validateEmailTemplates?: boolean;
  lockedChannel?: DeliveryChannel;
  allowedChannels?: DeliveryChannel[];
};

/**
 * Eine gemeinsame Optionsquelle für alle Einzelkanal-Auswahlen.
 * Jede registrierte LetterSending-Integration wird als eigene Kachel angeboten.
 */
export function useDeliveryChannelTileOptions({
  integrations,
  personEmail,
  emailValidation,
  validateEmailTemplates = true,
  lockedChannel,
  allowedChannels
}: DeliveryChannelTileOptionsParams): OutcomeTileOption<DeliveryChannel>[] {
  const { text } = useI18n();
  const hasEmailAddress = Boolean(String(personEmail || "").trim());
  const emailBlockedReason = !validateEmailTemplates
    ? null
    : !hasEmailAddress
      ? text("No email address on file.")
      : emailValidation && !emailValidation.isValid
        ? emailValidation.errors.join("\n")
        : null;

  return React.useMemo(() => {
    const channels = orderDeliveryChannels<DeliveryChannel>(
      allowedChannels ?? [
        "email",
        "print",
        ...integrations.map((integration) => `integration:${integration.id}` as DeliveryChannel)
      ]
    );
    return channels.map((channel) => {
      if (channel === "email") {
        return {
          value: channel,
          label: text("Email"),
          icon: <MailOutlined />,
          disabled: Boolean(lockedChannel && lockedChannel !== channel) || Boolean(emailBlockedReason),
          tooltip: emailBlockedReason || undefined
        };
      }
      if (channel === "print") {
        return {
          value: channel,
          label: text("Letter (print)"),
          icon: <PrinterOutlined />,
          disabled: Boolean(lockedChannel && lockedChannel !== channel)
        };
      }
      if (channel === "debt_collection") {
        return {
          value: channel,
          label: text("Debt collection handover"),
          icon: <AuditOutlined />,
          disabled: Boolean(lockedChannel && lockedChannel !== channel)
        };
      }
      const integrationId = channel.slice("integration:".length).toLowerCase();
      const integration = integrations.find((candidate) => candidate.id.toLowerCase() === integrationId);
      return {
        value: channel,
        label: integration
          ? text("Send via {0}").replace("{0}", integration.displayName)
          : text("Unavailable delivery integration"),
        icon: <SendOutlined />,
        disabled: Boolean(lockedChannel && lockedChannel !== channel) || !integration
      };
    });
  }, [allowedChannels, emailBlockedReason, integrations, lockedChannel, text]);
}

export function DeliveryChannelTileSelector({
  value,
  onChange,
  ...optionsParams
}: DeliveryChannelTileOptionsParams & {
  value?: DeliveryChannel | null;
  onChange?: (value: DeliveryChannel) => void;
}) {
  const options = useDeliveryChannelTileOptions(optionsParams);
  return <OutcomeTileSelector<DeliveryChannel> value={value} onChange={onChange} options={options} />;
}

/**
 * Übersetzt den gewählten Einzelkanal in die bestehenden Backend-Flags
 * (sendEmail/sendPrint/sendApi + integrationId) — der API-Vertrag bleibt unverändert.
 */
export function deliveryChannelToSendFlags(
  channel: DeliveryChannel | null | undefined
): { sendEmail: boolean; sendPrint: boolean; sendApi: boolean; integrationId: string | null } {
  if (!channel) return { sendEmail: false, sendPrint: false, sendApi: false, integrationId: null };
  if (channel === "email") return { sendEmail: true, sendPrint: false, sendApi: false, integrationId: null };
  if (channel === "print") return { sendEmail: false, sendPrint: true, sendApi: false, integrationId: null };
  if (channel.startsWith("integration:")) {
    return { sendEmail: false, sendPrint: false, sendApi: true, integrationId: channel.slice("integration:".length) };
  }
  return { sendEmail: false, sendPrint: false, sendApi: false, integrationId: null };
}

/** Zuletzt gewählten Kanal je Nutzer merken (neue Keys; die alten Mehrkanal-Keys verfallen bewusst). */
export function loadStoredDeliveryChannel(storageKey: string, userId: string | undefined): DeliveryChannel | null {
  if (!storageKey || !userId || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(`${storageKey}.${userId}`);
    if (!raw) return null;
    if (raw === CHANNEL_EMAIL_LC || raw === DELIVERY_CHANNEL_PRINT || raw.startsWith("integration:")) return raw as DeliveryChannel;
    return null;
  } catch {
    // Storage nicht verfügbar (z. B. Privacy-Modus) — dann eben keine Vorbelegung.
    return null;
  }
}

export function saveStoredDeliveryChannel(storageKey: string, userId: string | undefined, channel: DeliveryChannel): void {
  if (!storageKey || !userId || typeof localStorage === "undefined") return;
  if (channel === "debt_collection") return; // Sonderfall Inkasso nicht als Präferenz merken
  try {
    localStorage.setItem(`${storageKey}.${userId}`, channel);
  } catch {
    // ignore — Vorbelegung ist reiner Komfort
  }
}

/**
 * Der Systemdokument-Vertrag trägt sein Erscheinungsbild selbst. Für den E-Mail-Kanal bleibt
 * deshalb nur die Empfängeradresse als lokale Voraussetzung; die fachliche Dokumentprüfung
 * erfolgt beim Versand im Backend.
 */
export function useEmailChannelValidation(params: {
  enabled: boolean;
  personEmail?: string | null;
  studioId?: string | null;
  usageFlag?: number;
}): { emailValidation: EmailChannelValidation; validatingEmail: boolean } {
  void params;
  return { emailValidation: null, validatingEmail: false };
}

export type DeliveryChannelSectionProps = {
  /** Drawer/Modal sichtbar — steuert die E-Mail-Validierung. */
  open: boolean;
  form: FormInstance;
  /** Studio-Kontext für die E-Mail-Template-Prüfung. */
  studioId?: string | null;
  personEmail?: string | null;
  /** Verfügbare Brief-Versand-Integrationen (je eine Kanal-Kachel). */
  integrations: DeliveryIntegration[];
  /** Varianten des Schreibens; leere Liste = nur der Grundfall, dann entfällt die Auswahl. */
  variantOptions: DeliveryVariantOption[];
  variantLabel: React.ReactNode;
  variantFieldName?: string;
  channelFieldName?: string;
  variantExtra?: React.ReactNode;
  channelExtra?: React.ReactNode;
  /** Kanal fest vorgegeben (z. B. Inkasso-Übergabe) — Kacheln gesperrt. */
  lockedChannel?: DeliveryChannel;
  /** Kanäle einschränken (Mahnung: allowedChannels vom Server). Ohne Angabe: email + print + Integrationen. */
  allowedChannels?: DeliveryChannel[];
  /** LocalStorage-Key-Präfix zum Merken des zuletzt gewählten Kanals. */
  storageKey?: string;
  /** E-Mail-Template-Prüfung überspringen (Mahnung: Server entscheidet über allowedChannels). */
  validateEmailTemplates?: boolean;
  usageFlag?: number;
  /** Variantenauswahl ausblenden (z. B. Inkasso-Übergabe ohne Schreiben). */
  showVariant?: boolean;
  /**
   * Bietet „vor dem Versand bearbeiten" an. Der gepflegte Text ist die Vorgabe, nicht das letzte
   * Wort — ein Einzelfall braucht manchmal einen Zusatz, ohne dass die Vorlage für alle anderen
   * geändert wird.
   * <para>
   * Aus, wo der Wortlaut nicht verhandelbar ist (Vertragsabschluss) und bei Serienversand: Dort
   * gäbe es kein einzelnes Schreiben, das man ansehen könnte.
   * </para>
   */
  showEditBeforeSend?: boolean;
  editBeforeSendFieldName?: string;
};

/**
 * Rendert die Form.Items für Vorlage + Versandkanal. Muss innerhalb der zugehörigen <Form> stehen.
 * Der Kanal liegt im Formularfeld `channelFieldName` (Default „deliveryChannel") als DeliveryChannel.
 */
export function DeliveryChannelSection({
  open,
  form,
  studioId,
  personEmail,
  integrations,
  variantOptions,
  variantLabel,
  variantFieldName = "variantId",
  channelFieldName = "deliveryChannel",
  variantExtra,
  channelExtra,
  lockedChannel,
  allowedChannels,
  storageKey,
  validateEmailTemplates = true,
  usageFlag,
  showVariant = true,
  showEditBeforeSend = false,
  editBeforeSendFieldName = "editBeforeSend"
}: DeliveryChannelSectionProps) {
  const auth = useAuth();
  const { text } = useI18n();
  const watchedChannel = Form.useWatch(channelFieldName, form) as DeliveryChannel | undefined;

  const { emailValidation, validatingEmail } = useEmailChannelValidation({
    enabled: open && validateEmailTemplates,
    personEmail,
    studioId,
    usageFlag
  });

  const channelOptions = useDeliveryChannelTileOptions({
    integrations,
    personEmail,
    emailValidation,
    validateEmailTemplates,
    lockedChannel,
    allowedChannels
  });

  const selectedChannelOption = channelOptions.find((option) => option.value === watchedChannel);

  /*
    Versendet wird eine angelegte Vorlage, nichts anderes. Ein „Standardschreiben" gibt es in der
    Auswahl deshalb nicht — die Klassifizierung der Vorlagen liefert genau die Schreiben, die zu
    diesem Vorgang gehören, und eines davon ist zu wählen. Steht nur eines zur Verfügung, ist es
    vorbelegt; eine Wahl ohne Alternative wäre nur eine Klickpflicht.
  */
  const singleVariantValue =
    showVariant && variantOptions.length === 1 ? String(variantOptions[0].value) : null;
  React.useEffect(() => {
    if (!open || !singleVariantValue) return;
    if (!form.getFieldValue(variantFieldName)) form.setFieldValue(variantFieldName, singleVariantValue);
  }, [open, form, singleVariantValue, variantFieldName]);

  const variantField = showVariant && variantOptions.length > 0 ? (
    <Form.Item
      name={variantFieldName}
      label={variantLabel}
      extra={variantExtra}
      rules={[{ required: true, message: text("Please select a text set") }]}
    >
      <Select
        showSearch
        optionFilterProp="label"
        placeholder={text("Select text set…")}
        options={variantOptions}
      />
    </Form.Item>
  ) : null;

  /*
    Erst wenn ein Kanal steht, gibt es ein Schreiben, das man ansehen könnte — vorher wäre der
    Schalter eine Zusage ohne Gegenstand. Er bleibt trotzdem sichtbar (nur gesperrt), damit die
    Zeile neben der Variantenauswahl nicht nachträglich aufspringt.
  */
  const editBeforeSendField = showEditBeforeSend ? (
    // Direktes Grid-Kind: nur dann greift die Abstandsregel des Grids, und „alignSelf: end" setzt
    // die Checkbox auf die Unterkante der Zeile — also auf Feldhöhe statt auf Labelhöhe.
    <Form.Item name={editBeforeSendFieldName} valuePropName="checked" style={{ alignSelf: "end" }}>
      <Checkbox disabled={!watchedChannel}>{text("Edit before sending")}</Checkbox>
    </Form.Item>
  ) : null;

  return (
    <>
      {variantField || editBeforeSendField ? (
        // Das Grid nimmt seinen Kindern den Ant-Standardabstand ab; der Abstand zum folgenden
        // Feld muss deshalb hier stehen.
        <div style={{ marginBottom: spacing.xl }}>
          <ResponsiveFormGrid columns={2}>
            {variantField}
            {editBeforeSendField}
          </ResponsiveFormGrid>
        </div>
      ) : null}
      <Form.Item
        name={channelFieldName}
        label={text("Delivery channel")}
        rules={[
          { required: true, message: text("Required") },
          {
            validator: async (_, value) => {
              const option = channelOptions.find((candidate) => candidate.value === value);
              if (!option || option.disabled) {
                throw new Error(
                  typeof option?.tooltip === "string"
                    ? option.tooltip
                    : text("Please select a delivery channel.")
                );
              }
            }
          }
        ]}
        extra={channelExtra}
        validateStatus={selectedChannelOption?.disabled ? "error" : undefined}
        help={selectedChannelOption?.disabled ? selectedChannelOption.tooltip || text("Please select a delivery channel.") : undefined}
      >
        {/* value/onChange injiziert Form.Item; der eigene Handler ergänzt nur die Kanal-Merkung. */}
        <OutcomeTileSelector<string>
          options={channelOptions}
          onChange={(value) => {
            if (storageKey) saveStoredDeliveryChannel(storageKey, auth.info?.accountId, value as DeliveryChannel);
          }}
        />
      </Form.Item>
      {validatingEmail ? (
        <Typography.Paragraph type="secondary">{text("Checking email availability…")}</Typography.Paragraph>
      ) : null}
    </>
  );
}

/**
 * Standardblock „Bestätigung senden" (Kündigung/Ruhezeit): Checkbox + DeliveryChannelSection,
 * nur sichtbar solange die Checkbox aktiv ist und eine Vorlage gewählt wurde.
 */
export function ConfirmationDeliverySection(props: DeliveryChannelSectionProps & {
  sendConfirmationFieldName?: string;
  heading?: React.ReactNode;
}) {
  const { text } = useI18n();
  const { sendConfirmationFieldName = "sendConfirmation", heading, ...sectionProps } = props;
  return (
    <>
      {heading}
      <Form.Item name={sendConfirmationFieldName} valuePropName="checked">
        <Checkbox>{text("Send confirmation")}</Checkbox>
      </Form.Item>
      <Form.Item noStyle shouldUpdate={(prev, curr) => prev[sendConfirmationFieldName] !== curr[sendConfirmationFieldName]}>
        {({ getFieldValue }) => {
          if (!getFieldValue(sendConfirmationFieldName)) return null;
          return <DeliveryChannelSection {...sectionProps} />;
        }}
      </Form.Item>
    </>
  );
}
